export const SOURCE_LOCALE = 'en-US' as const;
export const DEFAULT_LOCALE = SOURCE_LOCALE;
export const PSEUDO_LOCALE = 'en-XA' as const;

export const PRODUCTION_LOCALES = [
  SOURCE_LOCALE,
  'hi-IN',
  'te-IN',
  'es-419',
  'de-DE',
] as const;

export const ALL_LOCALES = [
  ...PRODUCTION_LOCALES,
  PSEUDO_LOCALE,
] as const;

export type ProductionLocale = typeof PRODUCTION_LOCALES[number];
export type AppLocale = typeof ALL_LOCALES[number];
export type LocaleDirection = 'ltr' | 'rtl';

export interface LocaleDefinition {
  code: AppLocale;
  englishName: string;
  nativeName: string;
  direction: LocaleDirection;
  isProduction: boolean;
}

export const LOCALE_REGISTRY: Record<AppLocale, LocaleDefinition> = {
  'en-US': {
    code: 'en-US',
    englishName: 'English (United States)',
    nativeName: 'English (United States)',
    direction: 'ltr',
    isProduction: true,
  },
  'hi-IN': {
    code: 'hi-IN',
    englishName: 'Hindi (India)',
    nativeName: 'हिन्दी (भारत)',
    direction: 'ltr',
    isProduction: true,
  },
  'te-IN': {
    code: 'te-IN',
    englishName: 'Telugu (India)',
    nativeName: 'తెలుగు (భారతదేశం)',
    direction: 'ltr',
    isProduction: true,
  },
  'es-419': {
    code: 'es-419',
    englishName: 'Spanish (Latin America)',
    nativeName: 'Español (Latinoamérica)',
    direction: 'ltr',
    isProduction: true,
  },
  'de-DE': {
    code: 'de-DE',
    englishName: 'German (Germany)',
    nativeName: 'Deutsch (Deutschland)',
    direction: 'ltr',
    isProduction: true,
  },
  'en-XA': {
    code: 'en-XA',
    englishName: 'Pseudo Locale',
    nativeName: '[!! Pseudo Locale !!]',
    direction: 'ltr',
    isProduction: false,
  },
};

const EXACT_LOCALE_ALIASES: Record<string, AppLocale> = {
  'de': 'de-DE',
  'de-de': 'de-DE',
  'en': 'en-US',
  'en-us': 'en-US',
  'en-xa': 'en-XA',
  'es': 'es-419',
  'es-419': 'es-419',
  'es-mx': 'es-419',
  'es-us': 'es-419',
  'hi': 'hi-IN',
  'hi-in': 'hi-IN',
  'te': 'te-IN',
  'te-in': 'te-IN',
};

// FT-265: In development, expose the pseudolocale (en-XA) in the language
// selector so QA can toggle it without manually typing the locale code.
// In production builds it is hidden — only production locales are visible.
const isDev = process.env.NODE_ENV !== 'production';

export const VISIBLE_LANGUAGE_SELECTOR_LOCALES: AppLocale[] = isDev
  ? [...PRODUCTION_LOCALES, PSEUDO_LOCALE]
  : [...PRODUCTION_LOCALES];

export const isSupportedLocale = (value: string | null | undefined): value is AppLocale => {
  if (!value) {
    return false;
  }

  return Object.prototype.hasOwnProperty.call(LOCALE_REGISTRY, value);
};

export const normalizeLocaleTag = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  return value.trim().replace(/_/g, '-').toLowerCase();
};

export const resolveSupportedLocale = (requestedLocale: string | null | undefined): AppLocale => {
  const normalized = normalizeLocaleTag(requestedLocale);
  if (!normalized) {
    return DEFAULT_LOCALE;
  }

  const exactMatch = EXACT_LOCALE_ALIASES[normalized];
  if (exactMatch) {
    return exactMatch;
  }

  const languageOnly = normalized.split('-')[0];
  const languageFallback = EXACT_LOCALE_ALIASES[languageOnly];
  if (languageFallback) {
    return languageFallback;
  }

  return DEFAULT_LOCALE;
};

